import SearchDropdown from '@allesauto/core/searchDropdown';
import { type Option } from '@allesauto/core/searchDropdown';
import translate from '@allesauto/i18n';
import { useAccessoriesCategories } from '@allesauto/utils/apiHooks';
import { ToastifyType } from '@allesauto/utils/consts';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

interface CategoryDropdownProps {
  fetchParams: { dealerId: string; vin?: string; series?: string };
  parentCategoryId?: string;
  onSelect: (categoryId?: string) => void;
  lastFullCategoryId?: string;
}

const CategoryDropdown = ({
  fetchParams,
  parentCategoryId,
  onSelect,
  lastFullCategoryId,
}: CategoryDropdownProps) => {
  const intl = useIntl();

  const {
    result = [],
    loading,
    error,
  } = useAccessoriesCategories({
    ...fetchParams,
    categoryId: parentCategoryId,
  });

  const [isTouched, setIsTouched] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const selectedCategory = useMemo(
    () =>
      selectedOption
        ? result.find((c) => c.categoryId === selectedOption.value)
        : undefined,
    [result, selectedOption]
  );

  useEffect(() => {
    if (!isTouched) {
      return;
    }

    if (selectedCategory?.ownCount) {
      onSelect(selectedCategory.categoryId);
    } else {
      onSelect(lastFullCategoryId);
    }
  }, [onSelect, selectedCategory, isTouched, lastFullCategoryId]);

  useEffect(() => {
    if (error) {
      import('@allesauto/core/toastify').then((module) => {
        module.toastifyFactory(
          ToastifyType.ERROR,
          translate(intl, 'global.ERRORS.DEFAULT.HEADER'),
          translate(intl, 'global.ERRORS.DEFAULT.TEXT')
        );
      });
    }
  }, [intl, error]);

  return (
    <>
      <div>
        <SearchDropdown
          label={translate(
            intl,
            'ui.SHOPPING_CART.ACCESSORIES.' +
              (parentCategoryId ? 'SUBCATEGORY_LABEL' : 'CATEGORY_LABEL')
          )}
          options={result.map((c) => ({
            value: c.categoryId,
            label: translate(
              intl,
              'ui.SHOPPING_CART.ACCESSORIES.CATEGORY_ITEM',
              {
                title: c.title,
                count: c.count,
              }
            ),
          }))}
          disabled={loading || error}
          value={selectedOption}
          onChange={(v) => {
            setIsTouched(true);
            setSelectedOption(v as Option);
          }}
          isSearchable={false}
        />
      </div>

      {/* recursively render a subcategories dropdown, if a current level category was selected and it has more total products count than its own products count */}
      {selectedCategory &&
        selectedCategory.count > selectedCategory.ownCount && (
          <CategoryDropdownMemoized
            // always set a key prop, cos we always need to create a new component if the parent is changed, otherwise it will lead to bugs with the inner state
            key={selectedCategory.categoryId}
            fetchParams={fetchParams}
            parentCategoryId={selectedCategory.categoryId}
            lastFullCategoryId={
              selectedCategory.ownCount
                ? selectedCategory.categoryId
                : lastFullCategoryId
            }
            onSelect={onSelect}
          />
        )}

      {/* always render a dummy disabled dropdown, but only for the 2nd lavel. it's also replaced by the real one if neccessery */}
      {!parentCategoryId &&
        (!selectedCategory ||
          selectedCategory.count === selectedCategory.ownCount) && (
          <SearchDropdown
            label={translate(
              intl,
              'ui.SHOPPING_CART.ACCESSORIES.SUBCATEGORY_LABEL'
            )}
            disabled
            value={null}
            isSearchable={false}
          />
        )}
    </>
  );
};

const CategoryDropdownMemoized = React.memo(
  CategoryDropdown,
  (prev, next) => prev.parentCategoryId === next.parentCategoryId
);

export default CategoryDropdownMemoized;
